import axios from "axios";
import React, { useEffect, useState } from "react";

import {


  useNavigate,

  useParams,

} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { fetchPriceSummary } from "../../actions/priceActions";

import { createOrder } from "../../actions/orderActions";

import { useCart } from "../../components/CartContext";

import {

  useRestaurantSession,

} from "../../components/RestaurantSessionProvider";

import {

  isSessionExpiredError,

  getSessionExpiredMessage,

} from "../../utils/sessionErrorHandler";

import "./css/OrderScreen.css";

function OrderScreen() {

  const {

    restaurantId,

    tableId,

  } = useParams();

  const navigate =

    useNavigate();

  const dispatch =

    useDispatch();

  // =====================================================

  // DINING SESSION

  // =====================================================

  const {

    sessionToken,

  } = useRestaurantSession();

  // =====================================================

  // CART

  // =====================================================

  const {

    cart,

    cartCount,

    clearCart,

  } = useCart();

  // Always use the current server-calculated prices, not navigation state.
  const { loading: priceLoading, summary: priceSummary, error: priceError } =
    useSelector((state) => state.price || {});
  const [pricePending, setPricePending] = useState(true);
  const cartKey = JSON.stringify(cart.map((item) => [item.id, item.quantity]));

  useEffect(() => {
    if (!cart.length) {
      setPricePending(true);
      return;
    }
    let active = true;
    setPricePending(true);
    const items = cart.map((item) => ({ menu_item_id: item.id, quantity: item.quantity }));
    Promise.resolve(dispatch(fetchPriceSummary(restaurantId, items)))
      .catch(() => {})
      .finally(() => { if (active) setPricePending(false); });
    return () => { active = false; };
  }, [dispatch, restaurantId, cartKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = priceSummary?.subtotal;
  const tax = priceSummary?.tax;
  const serviceCharge = priceSummary?.service_charge;
  const total = priceSummary?.total;
  const priceReady = !pricePending && !priceLoading && !priceError &&
    priceSummary && Number.isFinite(Number(total));
  const displayPrice = (value) => priceReady ? Number(value || 0).toFixed(2) : "—";

  /*

  =====================================================

  STATE

  =====================================================

  */

  const [

    paymentMethod,

    setPaymentMethod,

  ] = useState("CASH");
  const [paymentOptions, setPaymentOptions] = useState(null);
  useEffect(() => {
    let active = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({data}) => {
        if (!active) return;
        const cash = data.allow_cash_payment === true;
        const online = data.allow_online_payment === true;
        setPaymentOptions({allow_cash_payment:cash, allow_online_payment:online});
        setPaymentMethod(current => current === "ONLINE" && online ? "ONLINE" :
          current === "CASH" && cash ? "CASH" : cash ? "CASH" : online ? "ONLINE" : "");
      })
      .catch(() => {if (active) {setPaymentOptions({allow_cash_payment:false,allow_online_payment:false});setPaymentMethod("");}});
    return () => {active = false;};
  }, [restaurantId]);


  const [

    notes,

    setNotes,

  ] = useState("");

  const [

    loading,

    setLoading,

  ] = useState(false);

  const [

    error,

    setError,

  ] = useState("");

  /*

  =====================================================

  BACK

  =====================================================

  */

  const handleBack = () => {

    navigate(

      `/${restaurantId}/table/${tableId}/checkout`

    );

  };

  /*

  =====================================================

  CREATE ORDER

  =====================================================

  */

  const handleCreateOrder = async () => {
    if (loading) return;
    if (!paymentOptions || !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]) {
      setError("This payment method is not available. Please select an enabled method.");
      return;
    }
    if (!priceReady) { setError("Final price is not ready. Please wait or try again."); return; }

    if (!cart || cart.length === 0) {

      setError(

        "Your cart is empty."

      );

      return;

    }

    // ---------------------------------------------------

    // CHECK DINING SESSION

    // ---------------------------------------------------

    if (!sessionToken) {

      setError(

        "Dining session is not ready. Please try again."

      );

      return;

    }

    try {

      setLoading(true);

      setError("");

      /*

      -----------------------------------------------

      ORDER PAYLOAD

      -----------------------------------------------

      */

      const orderData = {

        items: cart.map((item) => ({

          menu_item_id:

            item.id,

          quantity:

            item.quantity,

        })),

        // ---------------------------------------------

        // DINING SESSION

        // ---------------------------------------------

        session_token:

          sessionToken,

        payment_method:

          paymentMethod,

        table_id:

          Number(tableId),

        notes:

          notes.trim(),

      };

      /*

      -----------------------------------------------

      CREATE ORDER

      -----------------------------------------------

      */

      const response =

        await dispatch(

          createOrder(

            restaurantId,

            orderData

          )

        );

      /*

      -----------------------------------------------

      GET ORDER ID

      -----------------------------------------------

      */

      const createdOrder =

        response?.order ||

        response?.data?.order ||

        response?.data ||

        response;

      const orderId = createdOrder?.id || createdOrder?.order_id || response?.order_id;
      if (!orderId) throw new Error("Order was not confirmed by the server.");
      const confirmedTotal = Number(createdOrder?.total ?? total);

      /*

      -----------------------------------------------

      CASH

      -----------------------------------------------

      */

      if (

        paymentMethod === "CASH"

      ) {

        clearCart();

        navigate(

          `/${restaurantId}/table/${tableId}/success`,

          {

            state: {

              orderId:

                orderId,

              paymentMethod:

                "CASH",

              total:

                confirmedTotal,

            },

          }

        );

        return;

      }

      /*

      -----------------------------------------------

      ONLINE PAYMENT

      -----------------------------------------------

      */

      navigate(

        `/${restaurantId}/table/${tableId}/payment`,

        {

          state: {

            orderId:

              orderId,

            total:

              confirmedTotal,

          },

        }

      );

    } catch (err) {

      console.error(

        "Create order error:",

        err

      );

      // -------------------------------------------------

      // SESSION EXPIRED

      // -------------------------------------------------

      if (

        isSessionExpiredError(err)

      ) {

        setError(

          "Your table session has ended. " +

          "Please scan the table QR code again " +

          "to start a new session."

        );

        return;

      }

      // -------------------------------------------------

      // OTHER ERRORS

      // -------------------------------------------------

      setError(

        getSessionExpiredMessage(err)

      );

    } finally {

      setLoading(false);

    }

  };

  /*

  =====================================================

  EMPTY CART

  =====================================================

  */

  if (!cart || cart.length === 0) {

    return (

      <div className="theme3-order-page">

        <div className="theme3-order-container">

          <button

            type="button"

            className="theme3-order-back"

            onClick={() =>

              navigate(

                `/${restaurantId}/table/${tableId}`

              )

            }

          >

            <span>

              ←

            </span>

            BACK TO MENU

          </button>

          <div className="theme3-order-empty">

            <div className="theme3-order-empty-icon">

              🛒

            </div>

            <h1>

              YOUR CART IS EMPTY

            </h1>

            <p>

              Add some dishes before creating

              your order.

            </p>

            <button

              type="button"

              className="theme3-order-primary-button"

              onClick={() =>

                navigate(

                  `/${restaurantId}/table/${tableId}`

                )

              }

            >

              EXPLORE MENU

            </button>

          </div>

        </div>

      </div>

    );

  }

  return (

    <div className="theme3-order-page">

      <div className="theme3-order-container">

        {/* =================================================

            BACK

        ================================================= */}

        <button

          type="button"

          className="theme3-order-back"

          onClick={handleBack}

        >

          <span>

            ←

          </span>

          BACK TO CHECKOUT

        </button>

        {/* =================================================

            HEADING

        ================================================= */}

        <div className="theme3-order-heading">

          <span className="theme3-order-eyebrow">

            FINAL STEP

          </span>

          <h1>

            CREATE ORDER

          </h1>

          <p>

            Choose your payment method and

            place your order.

          </p>

        </div>

        {/* =================================================

            MAIN LAYOUT

        ================================================= */}

        <div className="theme3-order-layout">

          {/* =================================================

              LEFT SIDE

          ================================================= */}

          <div className="theme3-order-main">

            {/* =================================================

                PAYMENT METHOD

            ================================================= */}

            <section className="theme3-order-section">

              <div className="theme3-order-section-heading">

                <span>

                  01

                </span>

                <div>

                  <small>

                    PAYMENT

                  </small>

                  <h2>

                    PAYMENT METHOD

                  </h2>

                </div>

              </div>

              <div className="theme3-payment-options">

                {/* CASH */}

                <button

                  type="button"

                  className={

                    `theme3-payment-option ${

                      paymentMethod === "CASH"

                        ? "theme3-payment-option-active"

                        : ""

                    }`

                  }

                  disabled={!paymentOptions?.allow_cash_payment}
                  aria-disabled={!paymentOptions?.allow_cash_payment}
                  style={!paymentOptions?.allow_cash_payment ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                  onClick={() =>

                    paymentOptions?.allow_cash_payment && setPaymentMethod("CASH")

                  }

                >

                  <span className="theme3-payment-radio">

                    {paymentMethod === "CASH" && (

                      <span>

                      </span>

                    )}

                  </span>

                  <span className="theme3-payment-icon">

                    ₹

                  </span>

                  <span className="theme3-payment-content">

                    <strong>

                      CASH

                    </strong>

                    <small>

                      Pay at the restaurant

                    </small>

                  </span>

                  {paymentMethod === "CASH" && (

                    <span className="theme3-payment-check">

                      ✓

                    </span>

                  )}

                </button>

                {/* ONLINE */}

                <button

                  type="button"

                  className={

                    `theme3-payment-option ${

                      paymentMethod === "ONLINE"

                        ? "theme3-payment-option-active"

                        : ""

                    }`

                  }

                  disabled={!paymentOptions?.allow_online_payment}
                  aria-disabled={!paymentOptions?.allow_online_payment}
                  style={!paymentOptions?.allow_online_payment ? {opacity:0.4,cursor:"not-allowed"} : undefined}
                  onClick={() =>

                    paymentOptions?.allow_online_payment && setPaymentMethod("ONLINE")

                  }

                >

                  <span className="theme3-payment-radio">

                    {paymentMethod === "ONLINE" && (

                      <span>

                      </span>

                    )}

                  </span>

                  <span className="theme3-payment-icon">

                    ◇

                  </span>

                  <span className="theme3-payment-content">

                    <strong>

                      ONLINE PAYMENT

                    </strong>

                    <small>

                      Pay securely online

                    </small>

                  </span>

                  {paymentMethod === "ONLINE" && (

                    <span className="theme3-payment-check">

                      ✓

                    </span>

                  )}

                </button>

              </div>

            </section>

            {/* =================================================

                SPECIAL INSTRUCTIONS

            ================================================= */}

            <section className="theme3-order-section">

              <div className="theme3-order-section-heading">

                <span>

                  02

                </span>

                <div>

                  <small>

                    NOTES

                  </small>

                  <h2>

                    SPECIAL INSTRUCTIONS

                  </h2>

                </div>

              </div>

              <textarea

                className="theme3-order-notes"

                value={notes}

                onChange={(event) =>

                  setNotes(

                    event.target.value

                  )

                }

                placeholder="Any special instructions?"

                maxLength={500}

              />

              <div className="theme3-order-notes-count">

                {notes.length}/500

              </div>

            </section>

            {/* =================================================

                ERROR

            ================================================= */}

            {error && (

              <div className="theme3-order-error">

                {error}

              </div>

            )}

          </div>

          {/* =================================================

              RIGHT — SUMMARY

          ================================================= */}

          <aside className="theme3-order-summary">

            <div className="theme3-order-summary-heading">

              <span>

                ORDER SUMMARY

              </span>

              <small>

                {cartCount}{" "}

                {cartCount === 1

                  ? "ITEM"

                  : "ITEMS"}

              </small>

            </div>

            {/* SUBTOTAL */}

            <div className="theme3-order-summary-row">

              <span>

                SUBTOTAL

              </span>

              <strong>

                ₹{displayPrice(subtotal)}

              </strong>

            </div>

            {/* TAX */}

            <div className="theme3-order-summary-row">

              <span>

                TAX

              </span>

              <strong>

                ₹{displayPrice(tax)}

              </strong>

            </div>

            {/* SERVICE CHARGE */}

            <div className="theme3-order-summary-row">

              <span>

                SERVICE CHARGE

              </span>

              <strong>

                ₹{displayPrice(serviceCharge)}

              </strong>

            </div>

            <div className="theme3-order-summary-divider">

            </div>

            {/* TOTAL */}

            <div className="theme3-order-total">

              <div>

                <span>

                  TOTAL

                </span>

                <small>

                  INCLUDING ALL CHARGES

                </small>

              </div>

              <strong>

                ₹{displayPrice(total)}

              </strong>

            </div>

            {/* PAYMENT SELECTED */}

            <div className="theme3-selected-payment">

              <span>

                PAYMENT

              </span>

              <strong>

                {paymentMethod === "CASH"

                  ? "CASH"

                  : "ONLINE PAYMENT"}

              </strong>

            </div>

            {priceError && <div className="theme3-order-error" role="alert">Unable to calculate final price. Please try again.</div>}
          {/* CREATE ORDER */}

            <button

              type="button"

              className="theme3-order-create-button"

              onClick={

                handleCreateOrder

              }

              disabled={loading || !sessionToken || !priceReady}

            >

              {loading ? (

                <>

                  <span className="theme3-order-spinner">

                  </span>

                  CREATING ORDER...

                </>

              ) : (

                <>

                  CREATE ORDER

                  <span>

                    →

                  </span>

                </>

              )}

            </button>

            <p className="theme3-order-security-text">

              {paymentMethod === "CASH"

                ? "You will pay at the restaurant."

                : "You will continue to secure online payment after the order is created."}

            </p>

          </aside>

        </div>

      </div>

    </div>

  );

}

export default OrderScreen;